/* Een balk die boven de projectpagina blijft staan: de secties met hun aantallen,
 * de sectie waar je nu bent, en zodra de kop uit beeld is ook het project zelf. */

import { useEffect, useRef, useState, type RefObject } from 'react';
import { cn } from '@/lib/utils';

export interface Sectie {
  id: string;
  label: string;
  aantal?: number | null;
  live?: boolean;
}

/** Het scrollende element van de CRM-layout (<main>), of het venster. */
function scrollouder(el: HTMLElement | null): HTMLElement | null {
  let n = el?.parentElement ?? null;
  while (n) {
    const o = getComputedStyle(n).overflowY;
    if (o === 'auto' || o === 'scroll') return n;
    n = n.parentElement;
  }
  return null;
}

export function Sectiebalk({ secties, kop, titel, label = 'Secties van dit project' }: {
  secties: Sectie[];
  kop: RefObject<HTMLElement>;
  titel: string;
  /** Toegankelijke naam van de balk; de offertepagina gebruikt hem ook. */
  label?: string;
}) {
  const [actief, setActief] = useState<string | null>(null);
  const [kopWeg, setKopWeg] = useState(false);
  const sleutel = secties.map((s) => s.id).join(',');

  const balk = useRef<HTMLElement>(null);

  useEffect(() => {
    const root = scrollouder(kop.current);
    const kopObs = new IntersectionObserver(([e]) => setKopWeg(!e.isIntersecting), { root, threshold: 0 });
    if (kop.current) kopObs.observe(kop.current);

    /* De actieve sectie: die op de lijn net onder de balk ligt. Staan er twee
       naast elkaar (hoofdkolom en rechterkolom), dan telt de linker. */
    let frame = 0;
    const bepaal = () => {
      frame = 0;
      const lijn = (balk.current?.getBoundingClientRect().bottom ?? 0) + 12;
      const raak = sleutel
        .split(',')
        .map((id) => document.getElementById(id))
        .filter((el): el is HTMLElement => Boolean(el))
        .map((el) => ({ id: el.id, r: el.getBoundingClientRect() }))
        .filter(({ r }) => r.top <= lijn && r.bottom > lijn)
        .sort((a, b) => a.r.left - b.r.left);
      if (raak[0]) setActief(raak[0].id);
    };
    const opScroll = () => {
      if (!frame) frame = requestAnimationFrame(bepaal);
    };
    const bron: HTMLElement | Window = root ?? window;
    bron.addEventListener('scroll', opScroll, { passive: true });
    bepaal();
    return () => {
      kopObs.disconnect();
      bron.removeEventListener('scroll', opScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [kop, sleutel]);

  const ga = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <nav
      ref={balk}
      aria-label={label}
      className="sticky top-0 z-20 -mx-3 border-b bg-background px-3 sm:-mx-6 sm:px-6"
    >
      <div className="flex items-center gap-1 overflow-x-auto py-1.5">
        <span className={cn('mr-2 max-w-[260px] shrink-0 truncate text-sm font-semibold transition-opacity', kopWeg ? 'opacity-100' : 'pointer-events-none w-0 opacity-0')}>
          {titel}
        </span>
        {secties.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => ga(s.id)}
            aria-current={actief === s.id ? 'true' : undefined}
            className={cn(
              'flex h-8 shrink-0 items-center gap-1.5 rounded-md px-2.5 text-sm transition-colors',
              actief === s.id ? 'bg-muted font-medium text-foreground' : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
            )}
          >
            {s.live && <span className="h-1.5 w-1.5 rounded-full bg-green-600" aria-label="live" />}
            {s.label}
            {s.aantal != null && <span className="tabular-nums text-muted-foreground">{s.aantal}</span>}
          </button>
        ))}
      </div>
    </nav>
  );
}
